import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  Divider as MuiDivider,
  Grid as MuiGrid,
  AccordionSummary,
  Typography as MuiTypography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";

import Panel from "../../../components/panels/Panel";
import DataAdminTable from "../../../components/DataAdminTable";
import useFetchData from "../../../hooks/useFetchData";

const Divider = styled(MuiDivider)(spacing);
const Grid = styled(MuiGrid)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;

// Constants
const ENDPOINT = "cow-list-inputs";
const NDX_FIELD = "cow_input_ndx";
const TITLE = "COW Controls";
const DEFAULT_RESERVOIR_ID = 197;
const TABLE_HEIGHT = "calc(100vh - 64px - 48px - 106px - 48px - 64px - 204px)";
const PAGE_SIZE = 30;

function CowControls() {
  const { getAccessTokenSilently } = useAuth0();

  // Fetch reservoir locations for dropdown
  const [ReservoirLocations] = useFetchData("list-locations-for-cow", [], true);

  // Local state for table data
  const [tableData, setTableData] = useState([]);

  // Constants for reservoir location indexes
  const STAGECOACH_RESERVOIR_NDX = 197;
  const ELKHEAD_RESERVOIR_NDX = 127;

  // Check if a record is a Stagecoach record
  const isStagecoachRecord = (rowData) =>
    rowData.reservoir_location_ndx === STAGECOACH_RESERVOIR_NDX;

  const editTableColumns = [
    {
      title: "Legend Name",
      field: "cow_input_name",
      cellStyle: { minWidth: 200 },
      editComponent: (props) => (
        <TextField
          required
          fullWidth
          error={!props.value}
          helperText={!props.value ? "*Required field" : ""}
          value={props.value || ""}
          onChange={(e) => props.onChange(e.target.value)}
        />
      ),
      validate: (rowData) =>
        !rowData.cow_input_name
          ? {
              isValid: false,
            }
          : true,
    },
    {
      title: "Graph Color",
      field: "graph_color",
      cellStyle: { minWidth: 100 },
      render: (rowData) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: rowData.graph_color || "#000000",
              border: "1px solid #ccc",
              borderRadius: "3px",
            }}
          />
          <span>{rowData.graph_color}</span>
        </div>
      ),
      editComponent: (props) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="color"
            value={props.value || "#000000"}
            onChange={(e) => props.onChange(e.target.value)}
            style={{
              width: "40px",
              height: "40px",
              border: "1px solid #ccc",
              borderRadius: "3px",
              padding: "0",
              cursor: "pointer",
            }}
          />
          <TextField
            required
            error={!props.value || !props.value.match(/^#[0-9A-F]{6}$/i)}
            helperText={
              !props.value || !props.value.match(/^#[0-9A-F]{6}$/i)
                ? "*Required field (Hex format: #RRGGBB)"
                : ""
            }
            value={props.value || ""}
            onChange={(e) => props.onChange(e.target.value)}
            placeholder="#000000"
            style={{ minWidth: "120px" }}
          />
        </div>
      ),
      validate: (rowData) =>
        !rowData.graph_color || !rowData.graph_color.match(/^#[0-9A-F]{6}$/i)
          ? {
              isValid: false,
            }
          : true,
    },
    {
      title: "Legend Order",
      field: "cow_slice_order",
      cellStyle: { minWidth: 100 },
      type: "numeric",
      editComponent: (props) => (
        <TextField
          type="number"
          fullWidth
          value={props.value || ""}
          onChange={(e) => {
            const value =
              e.target.value === "" ? null : parseInt(e.target.value, 10);
            props.onChange(value);
          }}
          placeholder="Order"
          helperText="Lower numbers appear on the top in the graph, last in the legend, and last in the tooltip"
        />
      ),
    },
    {
      title: "Reservoir",
      field: "reservoir_location_ndx",
      cellStyle: { minWidth: 200 },
      lookup: ReservoirLocations.reduce((acc, location) => {
        acc[location.value] = location.display;
        return acc;
      }, {}),
      editComponent: (props) => {
        // Reservoir is always read-only (disabled) - defaults to Stagecoach for new records
        return (
          <FormControl fullWidth required>
            <InputLabel>Reservoir</InputLabel>
            <Select
              value={props.value || DEFAULT_RESERVOIR_ID}
              onChange={(e) => props.onChange(e.target.value)}
              disabled={true}
            >
              {ReservoirLocations.map((location) => (
                <MenuItem key={location.value} value={location.value}>
                  {location.display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      title: "Excel Column Name",
      field: "sc_colname",
      cellStyle: { minWidth: 150 },
      render: (rowData) => {
        // Only show for Stagecoach records
        if (!isStagecoachRecord(rowData)) return null;
        return rowData.sc_colname || "";
      },
      editComponent: (props) => {
        // For new records, check if reservoir will be Stagecoach
        const isNewRecord = !props.rowData.cow_input_ndx;
        const reservoirNdx =
          props.rowData.reservoir_location_ndx || DEFAULT_RESERVOIR_ID;
        const showField =
          isNewRecord || reservoirNdx === STAGECOACH_RESERVOIR_NDX;

        if (!showField) return null;

        return (
          <TextField
            required
            fullWidth
            error={!props.value}
            helperText={
              !props.value
                ? "*Required field (Match this to row 3 in the accounting workbook Push to Dashboard tab)"
                : "Match this to row 3 in the accounting workbook Push to Dashboard tab"
            }
            value={props.value || ""}
            onChange={(e) => props.onChange(e.target.value)}
          />
        );
      },
      validate: (rowData) => {
        // Only validate for Stagecoach records
        if (rowData.reservoir_location_ndx !== STAGECOACH_RESERVOIR_NDX) {
          return true;
        }
        return !rowData.sc_colname
          ? {
              isValid: false,
            }
          : true;
      },
    },
  ];

  const { data, error, isFetching, refetch } = useQuery(
    [ENDPOINT],
    async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/${ENDPOINT}`,
          { headers }
        );

        return data;
      } catch (err) {
        console.error(err);
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  // Update local state when data changes
  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  if (error) return "An error has occurred: " + error.message;

  return (
    <React.Fragment>
      <Helmet title={TITLE} />
      <Typography variant="h3" gutterBottom display="inline">
        {TITLE}
      </Typography>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="table-content"
              id="table-header"
            >
              <Typography variant="h4" ml={2}>
                Colors of Water Storage Accounts Configuration
              </Typography>
            </AccordionSummary>
            <Panel>
              <AccordionDetails>
                <TableWrapper>
                  <DataAdminTable
                    pageSize={PAGE_SIZE}
                    isLoading={isFetching}
                    label={TITLE}
                    columns={editTableColumns}
                    data={tableData}
                    height={TABLE_HEIGHT}
                    updateHandler={setTableData}
                    endpoint={ENDPOINT}
                    ndxField={NDX_FIELD}
                    handleRefresh={refetch}
                    allowAdd={true}
                    allowDelete={true}
                    isDeletable={(rowData) =>
                      rowData.reservoir_location_ndx !== ELKHEAD_RESERVOIR_NDX
                    }
                  />
                </TableWrapper>
              </AccordionDetails>
            </Panel>
          </Accordion>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default CowControls;
